import { createSelector } from 'reselect';

import { selectTargetPlatform } from '@pressreader/appcore';
import { selectGetConfigValue } from '@pressreader/config';
import { NavigationConfig, ViewName } from '@pressreader/navigation-types';
import { TargetPlatform } from '@pressreader/types';

import { INavigationConfigDto } from '../api/types';
import { NAVIGATION_CORE_MODULE_NAME } from '../constants';
import { isNavConfigWithArticlePopupId } from '../utils';

import { IState } from './reducer';

const getState = (state: Record<string, unknown>) => state[NAVIGATION_CORE_MODULE_NAME] as IState;

export const selectHandleRouting = createSelector(getState, state => state.handleRouting);

export const selectNavigationConfig = createSelector(selectGetConfigValue, selectTargetPlatform, (getConfigValue, platform): NavigationConfig => {
    const configDto = getConfigValue<INavigationConfigDto>('app.common.navigation');

    const featureFlagName = platform === TargetPlatform.Pervasive ? 'features.pervasive.home.view.name' : 'features.home.view.name';
    const homeViewName = getConfigValue<ViewName>(featureFlagName);

    if (!configDto || !configDto.landing_url_feature_key) {
        return { landingUrl: null, homeViewName };
    }

    return {
        landingUrl: getConfigValue<string | null>(configDto.landing_url_feature_key, null),
        homeViewName,
    };
});

export const selectViewName = createSelector(getState, state => state.viewName);
export const selectSubviewName = createSelector(getState, state => state.subviewName);
export const selectPrevNavConfig = createSelector(getState, state => state.prevNavConfig);
export const selectNavConfig = createSelector(getState, state => state.navConfig);
export const selectIsArticlePopupRouteOpen = createSelector(
    selectNavConfig,
    currentViewNavConfig => !!currentViewNavConfig && isNavConfigWithArticlePopupId(currentViewNavConfig),
);

export const selectLocation = createSelector(getState, state => state.location);
export const selectNavigationOrigin = createSelector(getState, state => state.navigationOrigin);
export const selectLocationState = createSelector(selectLocation, location => location.state);
export const selectNavigationHistoryState = createSelector(selectLocationState, selectViewName, (state, viewName) => ({
    state,
    stateLess: {},
    viewName: viewName === null ? ViewName.Error : viewName,
}));

export const selectNavConfigSuppressRefocus = createSelector(selectNavConfig, navConfig => navConfig?.suppressRefocus);

export const selectRefocusTargetActive = createSelector(getState, state => state.refocusTargetActive);

export const selectRefocusConfigActive = createSelector(getState, state => state.refocusConfigActive);

const selectRefocusingToTarget = createSelector(getState, state => state.refocusingTo);
export const selectIsRefocusingToNavConfigTarget = createSelector(selectRefocusingToTarget, Boolean);

export const selectIsNavigationInProgress = createSelector(getState, state => state.isNavigationInProgress);

export const selectCurrentNavId = createSelector(getState, state => state.currentNavId);

export const selectNavConfigHistory = createSelector(getState, state => state.navConfigHistory);

export const selectIsUpdatedNavConfigDifferentView = createSelector(selectNavConfigHistory, navConfigHistory => {
    if (navConfigHistory.length < 2) {
        return false;
    }
    const currentNavConfig = navConfigHistory[navConfigHistory.length - 1];
    if (isNavConfigWithArticlePopupId(currentNavConfig)) {
        return false;
    }
    const currentViewName = currentNavConfig.viewName;

    for (let i = navConfigHistory.length - 2; i >= 0; i--) {
        const previousNavConfig = navConfigHistory[i];
        if (previousNavConfig.viewName !== currentNavConfig.viewName) {
            return true;
        }
        if (previousNavConfig.viewName === currentViewName && !isNavConfigWithArticlePopupId(previousNavConfig)) {
            return false;
        }
    }
    return false;
});
